import React from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import { formatUiDateTime } from '../../../utils/dateTime';

const useStyles = makeStyles((theme) => {
    const isDark = theme.palette.type === 'dark';
    const borderColor = isDark ? 'rgba(148, 163, 184, 0.24)' : '#dfe6ee';
    const mutedSurface = isDark ? 'rgba(148, 163, 184, 0.08)' : '#f7f9fc';
    const hoverSurface = isDark ? 'rgba(61, 125, 221, 0.1)' : '#f7faff';

    return {
        section: {
            marginTop: theme.spacing(4),
            paddingTop: theme.spacing(3),
            borderTop: `1px solid ${borderColor}`,
        },
        sectionHeader: {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2.5),
            [theme.breakpoints.down('xs')]: {
                alignItems: 'flex-start',
                flexDirection: 'column',
            },
        },
        sectionTitle: {
            color: theme.palette.text.primary,
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: theme.spacing(0.5),
        },
        sectionDescription: {
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
        },
        historySummary: {
            flexShrink: 0,
            padding: theme.spacing(0.75, 1.25),
            borderRadius: 999,
            backgroundColor: mutedSurface,
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 600,
        },
        emptyState: {
            padding: theme.spacing(4, 3),
            border: `1px dashed ${borderColor}`,
            borderRadius: 10,
            backgroundColor: mutedSurface,
            color: theme.palette.text.secondary,
            textAlign: 'center',
        },
        companyCard: {
            marginBottom: theme.spacing(2.5),
            border: `1px solid ${borderColor}`,
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            boxShadow: isDark
                ? '0 4px 14px rgba(0, 0, 0, 0.16)'
                : '0 4px 14px rgba(30, 48, 70, 0.05)',
            overflow: 'hidden',
        },
        companyContent: {
            padding: theme.spacing(2.5),
            '&:last-child': {
                paddingBottom: theme.spacing(2.5),
            },
        },
        companyHeader: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',
            },
        },
        companyIdentity: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            minWidth: 0,
        },
        companyMark: {
            width: 36,
            height: 36,
            flex: '0 0 36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.22)' : '#eaf2ff',
            color: isDark ? '#b7d1ff' : theme.palette.primary.main,
            fontSize: '0.875rem',
            fontWeight: 800,
        },
        companyTitle: {
            color: theme.palette.text.primary,
            fontSize: '1.0625rem',
            fontWeight: 700,
        },
        companyCounts: {
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginTop: 2,
        },
        companyMeta: {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: theme.spacing(2),
            [theme.breakpoints.down('xs')]: {
                justifyContent: 'flex-start',
            },
        },
        metaItem: {
            minWidth: 150,
        },
        metaLabel: {
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            marginBottom: 3,
        },
        metaValue: {
            color: theme.palette.text.primary,
            fontSize: '0.8125rem',
            fontWeight: 500,
        },
        relationships: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(0.75),
            marginTop: theme.spacing(1.75),
        },
        relationshipChip: {
            height: 24,
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.2)' : '#eef5fd',
            color: isDark ? '#a9c8ff' : theme.palette.primary.main,
            fontSize: '0.6875rem',
            fontWeight: 600,
        },
        projectList: {
            borderTop: `1px solid ${borderColor}`,
        },
        projectAccordion: {
            borderBottom: `1px solid ${borderColor}`,
            borderRadius: '0 !important',
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'none',
            margin: '0 !important',
            '&:before': {
                display: 'none',
            },
            '&:last-child': {
                borderBottom: 0,
            },
        },
        projectSummary: {
            minHeight: 64,
            padding: theme.spacing(0, 2.5),
            transition: 'background-color 150ms ease',
            '&:hover': {
                backgroundColor: hoverSurface,
            },
            '&.Mui-expanded': {
                minHeight: 64,
                backgroundColor: mutedSurface,
            },
            '& .MuiAccordionSummary-content': {
                margin: `${theme.spacing(1.5)}px 0 !important`,
            },
        },
        projectSummaryContent: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1.5),
            paddingRight: theme.spacing(1),
            [theme.breakpoints.down('xs')]: {
                alignItems: 'flex-start',
                flexDirection: 'column',
            },
        },
        projectSummaryMain: {
            minWidth: 0,
        },
        projectTitle: {
            color: theme.palette.text.primary,
            fontSize: '0.9375rem',
            fontWeight: 700,
        },
        projectSubtitle: {
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginTop: 3,
        },
        projectTags: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(0.75),
        },
        statusChip: {
            height: 24,
            fontSize: '0.6875rem',
            fontWeight: 600,
        },
        currentChip: {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.24)' : '#e8f1ff',
            color: isDark ? '#b7d1ff' : '#245fae',
        },
        deletedChip: {
            backgroundColor: isDark ? 'rgba(220, 77, 77, 0.2)' : '#fff0f0',
            color: isDark ? '#ffb4b4' : '#b83232',
        },
        projectDetails: {
            display: 'block',
            padding: theme.spacing(0, 2.5, 2.5),
        },
        projectMetaGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: theme.spacing(2),
            padding: theme.spacing(2, 0),
            marginBottom: theme.spacing(2),
            borderBottom: `1px solid ${borderColor}`,
        },
        planMeta: {
            minWidth: 220,
        },
        courseHeadingRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        courseHeading: {
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
            fontWeight: 700,
        },
        courseCount: {
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 600,
        },
        tableContainer: {
            width: `calc(100% + ${theme.spacing(5)}px)`,
            marginLeft: -theme.spacing(2.5),
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
        },
        table: {
            minWidth: 1120,
            '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
                backgroundColor: isDark ? 'rgba(148, 163, 184, 0.035)' : '#fbfcfe',
            },
            '& .MuiTableBody-root .MuiTableRow-root:hover': {
                backgroundColor: hoverSurface,
            },
        },
        tableHead: {
            backgroundColor: isDark ? '#15243a' : '#f2f6fa',
        },
        tableHeadCell: {
            color: `${theme.palette.text.primary} !important`,
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            padding: theme.spacing(1, 1.5),
        },
        tableCell: {
            color: theme.palette.text.primary,
            fontSize: '0.8125rem',
            verticalAlign: 'top',
            padding: theme.spacing(1.25, 1.5),
        },
        courseName: {
            fontWeight: 600,
        },
        recordType: {
            display: 'inline-flex',
            marginBottom: theme.spacing(0.75),
        },
        detailLine: {
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            lineHeight: 1.5,
            marginBottom: 2,
        },
        detailLineLabel: {
            color: theme.palette.text.primary,
            fontWeight: 600,
        },
        projectEmptyState: {
            padding: theme.spacing(2.5),
            color: theme.palette.text.secondary,
            fontSize: '0.8125rem',
            textAlign: 'center',
        },
    };
});

const hasRecordedValue = (value) => value !== null && value !== undefined && value !== '';

export const formatHistoryDate = (value, fallback = 'Not recorded') => {
    if (!hasRecordedValue(value)) return fallback;
    return formatUiDateTime(value);
};

export const formatProgress = (value) => (
    hasRecordedValue(value) ? `${value}%` : 'Not recorded'
);

export const formatEmailSent = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'No record';
};

const humanize = (value, fallback = 'Not recorded') => {
    if (!hasRecordedValue(value)) return fallback;
    const text = String(value).replace(/_/g, ' ').trim();
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : fallback;
};

const formatCount = (count, singular) => {
    if (count === 1) return `${count} ${singular}`;
    const plural = singular.endsWith('y') ? `${singular.slice(0, -1)}ies` : `${singular}s`;
    return `${count} ${plural}`;
};

const getCourseRecords = (project) => {
    if (Array.isArray(project?.courses)) return project.courses;
    if (Array.isArray(project?.course_records)) return project.course_records;
    return [];
};

const LearningHubPlan = ({ startDate, endDate, classes }) => {
    if (!hasRecordedValue(startDate) && !hasRecordedValue(endDate)) return null;

    return (
        <div className={`${classes.metaItem} ${classes.planMeta}`}>
            <span className={classes.metaLabel}>Learning Hub plan</span>
            <span className={classes.metaValue}>
                {hasRecordedValue(startDate) ? `Starts ${formatHistoryDate(startDate)}` : null}
                {hasRecordedValue(startDate) && hasRecordedValue(endDate) ? ' · ' : null}
                {hasRecordedValue(endDate) ? `Ends ${formatHistoryDate(endDate)}` : null}
            </span>
        </div>
    );
};

LearningHubPlan.propTypes = {
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    classes: PropTypes.object.isRequired,
};

const CourseRecordRow = ({ course, classes }) => {
    const isLicence = course.record_type === 'licence';
    const isLearningHub = course.record_type === 'learning_hub';
    const activityLabel = isLicence ? 'Licence assigned' : isLearningHub ? 'Enrolled' : 'Activity date';
    const activityDate = isLicence ? course.license_assigned_at : isLearningHub ? course.enrolled_at : null;
    const recordStatusLabel = isLicence ? 'Licence status' : isLearningHub ? 'Enrollment status' : 'Record status';
    const recordStatus = isLicence ? course.license_status : isLearningHub ? course.enrollment_status : null;
    const hasPool = isLicence && (
        hasRecordedValue(course.licence_seats_initial)
        || hasRecordedValue(course.licence_seats_remaining)
    );

    return (
        <TableRow>
            <TableCell className={classes.tableCell}>
                <Chip
                    className={`${classes.statusChip} ${classes.recordType}`}
                    size="small"
                    variant="outlined"
                    label={humanize(course.record_type)}
                />
                <span className={classes.detailLine}>
                    <span className={classes.detailLineLabel}>{recordStatusLabel}: </span>
                    {humanize(recordStatus)}
                </span>
            </TableCell>
            <TableCell className={`${classes.tableCell} ${classes.courseName}`}>
                {hasRecordedValue(course.course_name) ? course.course_name : 'Not recorded'}
            </TableCell>
            <TableCell className={classes.tableCell}>
                {hasRecordedValue(course.provider) ? course.provider : 'Not recorded'}
            </TableCell>
            <TableCell className={classes.tableCell}>{humanize(course.completion_status)}</TableCell>
            <TableCell className={classes.tableCell}>{formatProgress(course.progress_percentage)}</TableCell>
            <TableCell className={classes.tableCell}>{humanize(course.certification_status)}</TableCell>
            <TableCell className={classes.tableCell}>{formatEmailSent(course.is_email_sent)}</TableCell>
            <TableCell className={classes.tableCell}>{formatHistoryDate(course.email_sent_at, 'No record')}</TableCell>
            <TableCell className={classes.tableCell}>
                <span className={classes.detailLine}>
                    <span className={classes.detailLineLabel}>{activityLabel}: </span>
                    {formatHistoryDate(activityDate)}
                </span>
                {hasRecordedValue(course.provider_course_expiration) ? (
                    <span className={classes.detailLine}>
                        <span className={classes.detailLineLabel}>Provider expiration: </span>
                        {course.provider_course_expiration}
                    </span>
                ) : null}
                {hasPool ? (
                    <span className={classes.detailLine}>
                        <span className={classes.detailLineLabel}>Pool: </span>
                        {hasRecordedValue(course.licence_seats_initial)
                            ? `${course.licence_seats_initial} initial`
                            : null}
                        {hasRecordedValue(course.licence_seats_initial) && hasRecordedValue(course.licence_seats_remaining)
                            ? ' · '
                            : null}
                        {hasRecordedValue(course.licence_seats_remaining)
                            ? `${course.licence_seats_remaining} remaining`
                            : null}
                    </span>
                ) : null}
            </TableCell>
        </TableRow>
    );
};

CourseRecordRow.propTypes = {
    course: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
};

const ProjectHistory = ({ project, classes }) => {
    const courseRecords = getCourseRecords(project);
    const projectName = hasRecordedValue(project.project_name) ? project.project_name : 'Unnamed project';

    return (
        <Accordion
            className={classes.projectAccordion}
            TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.projectSummary}
                aria-label={`${projectName} learning history`}
            >
                <div className={classes.projectSummaryContent}>
                    <div className={classes.projectSummaryMain}>
                        <Typography className={classes.projectTitle}>{projectName}</Typography>
                        <span className={classes.projectSubtitle}>
                            {humanize(project.status)} · {formatCount(courseRecords.length, 'course record')}
                        </span>
                    </div>
                    <div className={classes.projectTags}>
                        {project.is_current_profile_project === true ? (
                            <Chip
                                className={`${classes.statusChip} ${classes.currentChip}`}
                                size="small"
                                label="Current profile project"
                            />
                        ) : null}
                        {project.is_deleted === true ? (
                            <Chip
                                className={`${classes.statusChip} ${classes.deletedChip}`}
                                size="small"
                                label="Deleted"
                            />
                        ) : null}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails className={classes.projectDetails}>
                <div className={classes.projectMetaGrid}>
                    <div className={classes.metaItem}>
                        <span className={classes.metaLabel}>Project created</span>
                        <span className={classes.metaValue}>{formatHistoryDate(project.project_created_at)}</span>
                    </div>
                    <div className={classes.metaItem}>
                        <span className={classes.metaLabel}>Project start date</span>
                        <span className={classes.metaValue}>{formatHistoryDate(project.project_start_date)}</span>
                    </div>
                    <div className={classes.metaItem}>
                        <span className={classes.metaLabel}>Project end date</span>
                        <span className={classes.metaValue}>{formatHistoryDate(project.project_end_date)}</span>
                    </div>
                    <div className={classes.metaItem}>
                        <span className={classes.metaLabel}>Project assignment</span>
                        <span className={classes.metaValue}>
                            {formatHistoryDate(project.user_assigned_at, 'Project assignment date not recorded')}
                        </span>
                    </div>
                    <LearningHubPlan
                        startDate={project.learning_hub_access_start_date}
                        endDate={project.learning_hub_access_end_date}
                        classes={classes}
                    />
                </div>

                <div className={classes.courseHeadingRow}>
                    <Typography className={classes.courseHeading}>Course records</Typography>
                    <span className={classes.courseCount}>{formatCount(courseRecords.length, 'record')}</span>
                </div>
                {courseRecords.length ? (
                    <TableContainer className={classes.tableContainer}>
                        <Table className={classes.table} size="small" aria-label={`${project.project_name || 'Project'} course history`}>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.tableHeadCell}>Record</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Course</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Provider</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Completion</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Progress</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Certification</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Email sent</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Email sent at</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Access details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {courseRecords.map((course, courseIndex) => (
                                    <CourseRecordRow
                                        key={`${course.record_type || 'record'}-${course.course_uuid || course.course_id || course.course_name || 'course'}-${courseIndex}`}
                                        course={course}
                                        classes={classes}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <div className={classes.projectEmptyState}>No course history recorded for this project.</div>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

ProjectHistory.propTypes = {
    project: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
};

const UserLearningHistory = ({ details }) => {
    const classes = useStyles();

    if (details === undefined) return null;

    const companies = Array.isArray(details?.companies) ? details.companies : [];
    const totalProjects = companies.reduce((total, company) => (
        total + (Array.isArray(company.projects) ? company.projects.length : 0)
    ), 0);
    const totalCourseRecords = companies.reduce((companyTotal, company) => {
        const projects = Array.isArray(company.projects) ? company.projects : [];
        return companyTotal + projects.reduce((projectTotal, project) => (
            projectTotal + getCourseRecords(project).length
        ), 0);
    }, 0);

    return (
        <section className={classes.section} aria-labelledby="learning-history-title">
            <div className={classes.sectionHeader}>
                <div>
                    <Typography id="learning-history-title" component="h2" className={classes.sectionTitle}>
                        Learning history
                    </Typography>
                    <Typography className={classes.sectionDescription}>
                        Company, project, and course records available within your administrator access.
                    </Typography>
                </div>
                {companies.length ? (
                    <span className={classes.historySummary}>
                        {formatCount(companies.length, 'company')} · {formatCount(totalProjects, 'project')} · {formatCount(totalCourseRecords, 'course record')}
                    </span>
                ) : null}
            </div>

            {!companies.length ? (
                <div className={classes.emptyState}>
                    No learning history is available within your company or project access.
                </div>
            ) : companies.map((company, companyIndex) => {
                const projects = Array.isArray(company.projects) ? company.projects : [];
                const relationships = Array.isArray(company.relationships) ? company.relationships : [];
                const companyName = hasRecordedValue(company.company_name) ? company.company_name : 'Unnamed company';
                const companyCourseRecords = projects.reduce((total, project) => (
                    total + getCourseRecords(project).length
                ), 0);

                return (
                    <Card
                        key={`${company.company_uuid || company.company_id || company.company_name || 'company'}-${companyIndex}`}
                        className={classes.companyCard}
                        variant="outlined"
                    >
                        <CardContent className={classes.companyContent}>
                            <div className={classes.companyHeader}>
                                <div className={classes.companyIdentity}>
                                    <div className={classes.companyMark} aria-hidden="true">
                                        {companyName.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <Typography component="h3" className={classes.companyTitle}>
                                            {companyName}
                                        </Typography>
                                        <span className={classes.companyCounts}>
                                            {formatCount(projects.length, 'project')} · {formatCount(companyCourseRecords, 'course record')}
                                        </span>
                                    </div>
                                </div>
                                <div className={classes.companyMeta}>
                                    <div className={classes.metaItem}>
                                        <span className={classes.metaLabel}>Company created</span>
                                        <span className={classes.metaValue}>{formatHistoryDate(company.company_created_at)}</span>
                                    </div>
                                    <div className={classes.metaItem}>
                                        <span className={classes.metaLabel}>Association</span>
                                        <span className={classes.metaValue}>
                                            {formatHistoryDate(company.user_associated_at, 'Association date not recorded')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {relationships.length ? (
                                <div className={classes.relationships} aria-label="Company relationships">
                                    {relationships.map((relationship, relationshipIndex) => (
                                        <Chip
                                            key={`${relationship}-${relationshipIndex}`}
                                            className={classes.relationshipChip}
                                            size="small"
                                            label={humanize(relationship)}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </CardContent>
                        <div className={classes.projectList}>
                            {projects.length ? projects.map((project, projectIndex) => (
                                <ProjectHistory
                                    key={`${project.project_uuid || project.project_id || project.project_name || 'project'}-${projectIndex}`}
                                    project={project}
                                    classes={classes}
                                />
                            )) : (
                                <div className={classes.projectEmptyState}>No project history is available for this company.</div>
                            )}
                        </div>
                    </Card>
                );
            })}
        </section>
    );
};

UserLearningHistory.propTypes = {
    details: PropTypes.shape({
        companies: PropTypes.array,
    }),
};

export default UserLearningHistory;
